import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { CsvDownloadButton } from '@/components/venue/CsvDownloadButton'
import { SettlementCycleListTable } from '@/components/venue/SettlementCycleListTable'
import { useVenueSettlementCycles } from '@/hooks/useVenueSettlementCycles'
import { downloadVenueSettlementCyclesCsv } from '@/lib/venue/settlementApi'
import type { VenueSettlementCycleStatus } from '@/lib/venue/settlementTypes'
import {
  SETTLEMENT_CYCLE_STATUS_OPTIONS,
  defaultCyclesFilter,
  validateCyclesRange,
  type SettlementCyclesFilter,
} from '@/lib/venue/settlementViewModel'
import { useVenueContext } from '@/routes/venueContext'

/** 정산 확정·예정 — 'sales/cycles'(설계 §2). 회차 목록 + 다음 정산 예정 내역. */
export function VenueSalesCyclesPage() {
  const { venueId } = useVenueContext()

  const [appliedFilter, setAppliedFilter] = useState<SettlementCyclesFilter>(defaultCyclesFilter)
  const [monthInputs, setMonthInputs] = useState({ fromMonth: '', toMonth: '' })

  const cyclesQuery = useVenueSettlementCycles(venueId, appliedFilter)
  const rangeError = validateCyclesRange(monthInputs)

  const pages = cyclesQuery.data?.pages ?? []
  const cycles = pages.flatMap((page) => page.cycles)
  const lastPage = pages.at(-1)

  function applyRange() {
    if (validateCyclesRange(monthInputs)) return
    setAppliedFilter((current) => ({ ...current, ...monthInputs }))
  }

  function toggleStatus(status: VenueSettlementCycleStatus) {
    setAppliedFilter((current) => ({
      ...current,
      statuses: current.statuses.includes(status)
        ? current.statuses.filter((value) => value !== status)
        : [...current.statuses, status],
    }))
  }

  return (
    <>
      <PageHeader
        title="정산 확정·예정"
        description="회차별 정산 기준 기간·확정 금액·지급일·상태를 확인합니다."
        action={
          <CsvDownloadButton
            label="CSV 다운로드"
            onDownload={async () => {
              if (!lastPage) return
              await downloadVenueSettlementCyclesCsv(venueId, {
                fromMonth: lastPage.fromMonth,
                toMonth: lastPage.toMonth,
              })
            }}
          />
        }
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-cycles-from">
            시작 회차
          </label>
          <Input
            id="sales-cycles-from"
            type="month"
            value={monthInputs.fromMonth}
            onChange={(event) => {
              setMonthInputs((current) => ({ ...current, fromMonth: event.target.value }))
            }}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-slateish-600" htmlFor="sales-cycles-to">
            종료 회차
          </label>
          <Input
            id="sales-cycles-to"
            type="month"
            value={monthInputs.toMonth}
            onChange={(event) => {
              setMonthInputs((current) => ({ ...current, toMonth: event.target.value }))
            }}
          />
        </div>
        <Button size="md" disabled={Boolean(rangeError)} onClick={applyRange}>
          조회
        </Button>
      </div>

      {rangeError ? <p className="mb-3 text-xs text-danger-500">{rangeError}</p> : null}

      <div role="group" aria-label="정산 상태 필터" className="mb-4 flex flex-wrap gap-2">
        {SETTLEMENT_CYCLE_STATUS_OPTIONS.map((option) => {
          const selected = appliedFilter.statuses.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => {
                toggleStatus(option.value)
              }}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                selected
                  ? 'border-brand-500 bg-brand-50 text-brand-500'
                  : 'border-slateish-300 text-slateish-600 hover:bg-slateish-50'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>

      {cyclesQuery.isLoading ? <LoadingState rows={4} label="정산 회차를 불러오는 중" /> : null}

      {cyclesQuery.isError ? (
        <ErrorState
          error={cyclesQuery.error}
          onRetry={() => {
            void cyclesQuery.refetch()
          }}
        />
      ) : null}

      {lastPage ? (
        <SettlementCycleListTable
          cycles={cycles}
          next={lastPage.next}
          hasNextPage={cyclesQuery.hasNextPage ?? false}
          isFetchingNextPage={cyclesQuery.isFetchingNextPage}
          onLoadMore={() => {
            void cyclesQuery.fetchNextPage()
          }}
        />
      ) : null}
    </>
  )
}
